import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import SummaryWidget from 'modules/documents/components/BulkFiles/DocumentAssigner/SummaryWidget'
import { UploadProcess } from 'modules/documents/hooks/useGetUploadProcess'
import { UploadProcessDocument } from 'modules/documents/hooks/useGetUploadProcessDocuments'
import type { AssignEsignatureForm } from 'modules/documents/lib/forms/AssignEsignatureForm'

type ButtonTouchableProps = React.ComponentProps<typeof Button>

interface Props {
  selectedIdsForEsignature: number[]
  requestEsignatureForm: AssignEsignatureForm
  onSelectIdsForEsignature: (arg0: number[]) => void
  uploadProcess: UploadProcess
  uploadProcessDocument?: UploadProcessDocument
  disabled?: boolean
  onClick?: ButtonTouchableProps['onClick']
  label?: string
  recipientsCount?: number
  onSignersUpdateFinished?: () => void
}

const EsignatureRequester = ({
  selectedIdsForEsignature,
  requestEsignatureForm,
  onSelectIdsForEsignature,
  uploadProcess,
  uploadProcessDocument,
  disabled,
  onClick,
  label,
  recipientsCount = 0,
  onSignersUpdateFinished,
}: Props) => {
  const esignatureEnabled = hasFeature('esignature')
  const field = requestEsignatureForm.get('requestEsignature')

  return (
    <>
      {esignatureEnabled && (
        <Box flexDirection="column" gap="s16">
          {field.value && (
            <>
              <Text size="200" color="grey800">
                {i18n.tp('split_documents.assign_documents.request_esignatures_info', {
                  count: recipientsCount,
                })}
              </Text>
              <Box
                paddingY="s16"
                paddingX="s16"
                gap="s8"
                borderRadius={{ all: 'abs006' }}
                border={{
                  all: {
                    color: 'grey400',
                    width: 's1',
                    style: 'solid',
                  },
                }}
              >
                <Text.Normal weight="medium">
                  {i18n.t('split_documents.assign_documents.select_additional_signees')}
                </Text.Normal>
                <Text size="200" color="grey800">
                  {recipientsCount === 1
                    ? i18n.tp(
                        'split_documents.assign_documents.request_esignature_description_in_bulk_one_doc',
                        {
                          count: selectedIdsForEsignature.length,
                        }
                      )
                    : i18n.tp(
                        'split_documents.assign_documents.request_esignature_description_in_bulk_other_doc',
                        {
                          count: selectedIdsForEsignature.length,
                          documentsCount: recipientsCount,
                        }
                      )}
                </Text>
                <Box>
                  <EmployeeSelectorV2
                    affiliations={['employee', 'external']}
                    multiple
                    useAccessId
                    value={selectedIdsForEsignature}
                    onChange={(state) => {
                      if (state.type === 'manual') {
                        onSelectIdsForEsignature(state.ids)
                      }
                    }}
                    forcedFilters={{ accessStatus: ['active'] }}
                    options={{ disableSelectAll: true }}
                  />
                </Box>
              </Box>
            </>
          )}
          {uploadProcess.esignatureRequest && !label && (
            <SummaryWidget uploadProcess={uploadProcess} />
          )}
          {uploadProcess.esignatureRequest && label && (
            <Box
              padding="s24"
              borderRadius={{ all: 'abs008' }}
              background="grey000White"
              border={{
                all: {
                  width: 's1',
                  style: 'solid',
                  color: 'grey400',
                },
              }}
            >
              <SummaryWidget
                uploadProcess={uploadProcess}
                uploadProcessDocument={uploadProcessDocument}
                onSignersUpdateFinished={onSignersUpdateFinished}
              />
            </Box>
          )}
          {label && (
            <Box alignItems="flexEnd">
              <Button disabled={disabled} onClick={onClick}>
                {label}
              </Button>
            </Box>
          )}
        </Box>
      )}
      {!esignatureEnabled && label && (
        <Box flexDirection="column" gap="s16" alignItems="flexEnd">
          <Button disabled={disabled} onClick={onClick}>
            {label}
          </Button>
        </Box>
      )}
    </>
  )
}

export default observer(EsignatureRequester)
